/* eslint-disable @typescript-eslint/unbound-method */
import { ApolloProvider } from '@apollo/client'
import Companies from '@components/companies/Companies'
import DefaultPageProvider, { defaultStaticQueries } from '@components/DefaultPageProvider'
import ScheduleSearch, { ScheduleSearchProps } from '@components/schedule/ScheduleSearch'
import WebPageProvider from '@components/webpage/WebpageProvider'
import { prepareResults as preparePrefilledAlgoliaResults } from '@lib/algoliaPrefill'
import { addApolloState, ApolloClient, initializeApollo } from '@lib/apolloClient'
import getSchedulesArchiveWebPages from '@lib/schedulesArchive'
import { APOLLO_STATE_PROP_NAME, ServerProps } from '@lib/server'
import { WebPageByHostPathQuery } from '@ts/operations'
import { getServerPageGetTimeZone } from '@ts/operations-ssr'
import { isEqual } from 'lodash'
import { GetStaticPaths, GetStaticProps } from 'next'
import dynamic from 'next/dynamic'
import React from 'react'

const AttendeeSearch = dynamic(() => import('@components/attendees/AttendeesSearch'))

const RootWebPage = () => {
  return (
    <DefaultPageProvider>
      <WebPageProvider />
    </DefaultPageProvider>
  )
}

export default React.memo(RootWebPage, (prev: ServerProps, next: ServerProps) =>
  isEqual(prev[APOLLO_STATE_PROP_NAME], next[APOLLO_STATE_PROP_NAME])
)

// eslint-disable-next-line react/display-name
export const serverScheduleFor = (apolloClient: ApolloClient) => (props: ScheduleSearchProps) => (
  <ApolloProvider client={apolloClient}>
    <ScheduleSearch {...props} />
  </ApolloProvider>
)

const preloadWebPageQueries = async (webPage: WebPageByHostPathQuery, apollo: ApolloClient) => {
  const pageQueries = webPage?.webPageByHostPath?.content?.flatMap<Promise<void>>(item => {
    switch (item.__typename) {
      case 'ComponentWebElementsAttendeeSearchSidebarFilters': {
        return preparePrefilledAlgoliaResults(apollo, AttendeeSearch, item)
      }
      case 'ComponentWebElementsScheduleSearchSidebarFilters': {
        return (async () => {
          await getServerPageGetTimeZone({}, apollo)
          await preparePrefilledAlgoliaResults(apollo, serverScheduleFor(apollo), item)
        })()
      }
      case 'ComponentWebElementsCompanySearchSidebarFilters': {
        return preparePrefilledAlgoliaResults(apollo, Companies, item)
      }
      default:
        return []
    }
  })

  await Promise.all(pageQueries ?? [])
}

export const getStaticProps: GetStaticProps<ServerProps> = async ({ params }) => {
  const apolloClient = initializeApollo()

  const path = `/${((params?.rootWebPage as string[]) ?? []).join('/')}`
  const { webPage } = await defaultStaticQueries(apolloClient, path)
  await preloadWebPageQueries(webPage, apolloClient)

  return addApolloState(apolloClient, { props: {}, revalidate: 300 })
}

export const getStaticPaths: GetStaticPaths = async () => {
  const schedulesArchive = await getSchedulesArchiveWebPages()
  const schedulesArchiveParams =
    schedulesArchive?.map(scheduleWebPage => ({
      params: {
        rootWebPage: [scheduleWebPage?.path?.split('/')[1] || ''],
      },
    })) || []

  return Promise.resolve({
    fallback: true,
    paths: [
      { params: { rootWebPage: ['schedule'] } },
      { params: { rootWebPage: ['speakers'] } },
      { params: { rootWebPage: ['partners', 'event-partners'] } },
      { params: { rootWebPage: ['startups', 'featured-startups'] } },
    ].concat(schedulesArchiveParams),
  })
}
